import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import {
  SandboxApiService,
  type SandboxDashboardMeta,
  type SandboxDashboardResourceType,
  type SandboxDashboardSandbox,
  type SandboxDashboardSummaryResponse,
  type SandboxDashboardTrendsResponse,
  type SandboxDashboardUserSort,
  type SandboxDashboardUsersResponse,
  type SandboxDashboardWarning,
} from '@angular-workspace/services';
import {
  SandboxDashboardDropdownComponent,
  type SandboxDashboardDropdownOption,
} from './sandbox-dashboard-dropdown.component';
import {
  SANDBOX_TREND_CHART_MODES,
  buildSandboxTrendChart,
  type SandboxTrendChartMode,
} from './sandbox-dashboard-chart.util';
import {
  DEFAULT_SANDBOX_DASHBOARD_THEME,
  SANDBOX_DASHBOARD_THEME,
  sandboxDashboardThemeVars,
} from './sandbox-dashboard.theme';
import {
  SANDBOX_DASHBOARD_PERIODS,
  SANDBOX_STATE_ORDER,
  buildIstWindow,
  formatSandboxBytes,
  formatSandboxCores,
  formatSandboxGpuUnits,
  formatSandboxHours,
  formatSandboxNumber,
  isAbortError,
  sandboxDashboardErrorMessage,
  sandboxStateLabel,
  sandboxStateTone,
} from './sandbox-dashboard.util';

interface UserRowVm {
  userId: string;
  label: string;
  runningCpu: string;
  runningGpu: string;
  cpuHours: string;
  gpuHours: string;
}

interface SandboxRowVm {
  id: string;
  name: string;
  resourceType: string;
  state: string;
  stateLabel: string;
  stateTone: string;
  cpu: string;
  memory: string;
  gpu: string;
}

type Section = 'summary' | 'trends' | 'users' | 'sandboxes';

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Platform-admin sandbox usage dashboard (CPU/GPU sandboxes, session hours, per-user usage).
 * Colours come from {@link SANDBOX_DASHBOARD_THEME}; the host app must also provide
 * `provideEchartsCore(...)` for the daily-hours chart.
 */
@Component({
  selector: 'lib-sandbox-usage-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NgxEchartsDirective,
    SandboxDashboardDropdownComponent,
  ],
  templateUrl: './sandbox-usage-dashboard.component.html',
  styleUrl: './sandbox-usage-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SandboxUsageDashboardComponent implements OnInit, OnDestroy {
  private readonly api = inject(SandboxApiService);
  readonly theme =
    inject(SANDBOX_DASHBOARD_THEME, { optional: true }) ??
    DEFAULT_SANDBOX_DASHBOARD_THEME;
  readonly themeVars = sandboxDashboardThemeVars(this.theme);

  readonly periodOptions: SandboxDashboardDropdownOption[] =
    SANDBOX_DASHBOARD_PERIODS.map((p) => ({
      value: p.days,
      label: p.label,
    }));
  /** Pill filter options; both selected = `all` for the API. */
  readonly resourceOptions: SandboxDashboardDropdownOption[] = [
    { value: 'cpu', label: 'CPU' },
    { value: 'gpu', label: 'GPU' },
  ];
  readonly sortOptions: SandboxDashboardDropdownOption[] = [
    { value: 'bookedSessionHours:desc', label: 'Most session hours' },
    { value: 'runningSandboxes:desc', label: 'Most running sandboxes' },
    { value: 'email:asc', label: 'Email (A–Z)' },
  ];
  readonly pageSizeOptions: SandboxDashboardDropdownOption[] = [
    8, 16, 24, 48,
  ].map((size) => ({
    value: size,
    label: String(size),
    suffix: 'results/page',
  }));
  readonly stateLabel = sandboxStateLabel;
  readonly stateTone = sandboxStateTone;

  readonly resourceType = signal<SandboxDashboardResourceType>('all');
  readonly chartModeOptions: SandboxDashboardDropdownOption[] =
    SANDBOX_TREND_CHART_MODES.map((m) => ({
      value: m.value,
      label: m.label,
    }));
  readonly chartMode = signal<SandboxTrendChartMode>('grouped');
  readonly periodDays = signal(7);
  private window = buildIstWindow(7);

  readonly summary = signal<SandboxDashboardSummaryResponse | null>(null);
  readonly trends = signal<SandboxDashboardTrendsResponse | null>(null);
  readonly users = signal<SandboxDashboardUsersResponse | null>(null);
  readonly userSandboxes = signal<SandboxRowVm[]>([]);

  readonly loading = signal<Record<Section, boolean>>({
    summary: false,
    trends: false,
    users: false,
    sandboxes: false,
  });
  readonly errors = signal<Record<Section, string | null>>({
    summary: null,
    trends: null,
    users: null,
    sandboxes: null,
  });

  readonly searchQuery = signal('');
  readonly sort = signal<SandboxDashboardUserSort>('bookedSessionHours:desc');
  readonly page = signal(1);
  readonly pageSize = signal(8);
  readonly expandedUserId = signal<string | null>(null);

  private readonly controllers = new Map<Section, AbortController>();
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly showCpu = computed(() => this.resourceType() !== 'gpu');
  readonly showGpu = computed(() => this.resourceType() !== 'cpu');

  readonly selectedResources = computed<string[]>(() => {
    const type = this.resourceType();
    return type === 'all' ? ['cpu', 'gpu'] : [type];
  });

  readonly isPartial = computed(
    () =>
      !!(
        this.summary()?.meta?.partial ||
        this.trends()?.meta?.partial ||
        this.users()?.meta?.partial
      ),
  );

  readonly warnings = computed<SandboxDashboardWarning[]>(() => {
    const seen = new Set<string>();
    const all: SandboxDashboardWarning[] = [];
    const metas: (SandboxDashboardMeta | undefined)[] = [
      this.summary()?.meta,
      this.trends()?.meta,
      this.users()?.meta,
    ];
    for (const meta of metas) {
      for (const w of meta?.warnings ?? []) {
        const key = `${w.code}|${w.detail}`;
        if (!seen.has(key)) {
          seen.add(key);
          all.push(w);
        }
      }
    }
    return all;
  });

  readonly cards = computed(() => {
    const result = this.summary()?.result;
    const sandboxes = result?.current?.sandboxes;
    const hours = result?.period?.bookedSessionHours;
    const cpu = this.showCpu();
    const gpu = this.showGpu();
    const metric = (tone: 'cpu' | 'gpu', enabled: boolean, value: string) => ({
      tone,
      label: tone.toUpperCase(),
      value: enabled ? value : '—',
      disabled: !enabled,
    });
    // Both CPU and GPU always render; a deselected resource is shown disabled.
    return [
      {
        title: 'Running sandboxes',
        caption: 'Now',
        metrics: [
          metric('cpu', cpu, formatSandboxNumber(sandboxes?.runningCpu)),
          metric('gpu', gpu, formatSandboxNumber(sandboxes?.runningGpu)),
        ],
      },
      {
        title: 'Recorded session hours',
        caption: 'Selected period',
        metrics: [
          metric('cpu', cpu, formatSandboxHours(hours?.cpu)),
          metric('gpu', gpu, formatSandboxHours(hours?.gpu)),
        ],
      },
    ];
  });

  /** Rows for the "Current usage" card; CPU/GPU rows are disabled (not hidden) when deselected. */
  readonly usageRows = computed(() => {
    const result = this.summary()?.result;
    const current = result?.current;
    const requests = current?.runningRequests;
    const cpu = this.showCpu();
    const gpu = this.showGpu();
    return [
      {
        label: 'Running users',
        value: formatSandboxNumber(current?.runningUsers),
        disabled: false,
      },
      {
        label: 'Users with sessions',
        value: formatSandboxNumber(result?.period?.usersWithSessions),
        disabled: false,
      },
      {
        label: 'Requested CPU',
        value: cpu ? formatSandboxCores(requests?.cpuCores) : '—',
        disabled: !cpu,
      },
      {
        label: 'Requested memory',
        value: formatSandboxBytes(requests?.memoryBytes),
        disabled: false,
      },
      {
        label: 'Requested GPU',
        value: gpu ? formatSandboxGpuUnits(requests?.gpuResources) : '—',
        disabled: !gpu,
      },
    ];
  });

  readonly strip = computed(() => {
    const byState = this.summary()?.result?.current?.sandboxes?.byState ?? {};
    const states = SANDBOX_STATE_ORDER.map((state) => ({
      state,
      count: byState[state] ?? 0,
    })).filter((s) => s.count > 0);
    return { states };
  });

  readonly trendPoints = computed(() => this.trends()?.result?.points ?? null);

  readonly chartOptions = computed<EChartsOption | null>(() => {
    const points = this.trendPoints();
    if (!points || points.length === 0) return null;
    return buildSandboxTrendChart(this.chartMode(), {
      points,
      theme: this.theme,
      showCpu: this.showCpu(),
      showGpu: this.showGpu(),
      reduceMotion:
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    });
  });

  readonly userRows = computed<UserRowVm[]>(() =>
    (this.users()?.result ?? []).map((u) => ({
      userId: u.userId,
      label: u.email || u.userId,
      runningCpu: formatSandboxNumber(u.stats?.current?.sandboxes?.runningCpu),
      runningGpu: formatSandboxNumber(u.stats?.current?.sandboxes?.runningGpu),
      cpuHours: formatSandboxNumber(u.stats?.period?.bookedSessionHours?.cpu),
      gpuHours: formatSandboxNumber(u.stats?.period?.bookedSessionHours?.gpu),
    })),
  );

  readonly pagination = computed(() => this.users()?.paginationInfo ?? null);

  /** Sliding window of up to 5 consecutive page numbers around the current page. */
  readonly pageItems = computed<number[]>(() => {
    const p = this.pagination();
    if (!p || p.totalPages <= 1) return [];
    const linkCount = 5;
    let start = Math.max(1, p.page - Math.floor(linkCount / 2));
    const end = Math.min(p.totalPages, start + linkCount - 1);
    start = Math.max(1, end - linkCount + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });
  readonly userColumnCount = computed(
    () => 1 + (this.showCpu() ? 2 : 0) + (this.showGpu() ? 2 : 0),
  );

  ngOnInit(): void {
    this.reloadAll();
  }

  ngOnDestroy(): void {
    for (const controller of this.controllers.values()) controller.abort();
    this.controllers.clear();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  setResources(values: (string | number)[]): void {
    const hasCpu = values.includes('cpu');
    const hasGpu = values.includes('gpu');
    const next: SandboxDashboardResourceType =
      hasCpu && !hasGpu ? 'cpu' : hasGpu && !hasCpu ? 'gpu' : 'all';
    if (next === this.resourceType()) return;
    this.resourceType.set(next);
    this.reloadAll();
  }

  setChartMode(mode: string | number): void {
    this.chartMode.set(String(mode) as SandboxTrendChartMode);
  }

  setPeriod(days: number | string): void {
    const value = Number(days);
    if (!Number.isFinite(value) || value === this.periodDays()) return;
    this.periodDays.set(value);
    this.reloadAll();
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applySearch(), SEARCH_DEBOUNCE_MS);
  }

  applySearch(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.page.set(1);
    void this.loadUsers();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.applySearch();
  }

  setSort(value: string | number): void {
    this.sort.set(String(value) as SandboxDashboardUserSort);
    this.page.set(1);
    void this.loadUsers();
  }

  setPageSize(value: number | string): void {
    this.pageSize.set(Number(value));
    this.page.set(1);
    void this.loadUsers();
  }

  goToPage(page: number): void {
    const total = this.pagination()?.totalPages ?? 1;
    if (page < 1 || page > total || page === this.page()) return;
    this.page.set(page);
    void this.loadUsers();
  }

  toggleUser(userId: string): void {
    if (this.expandedUserId() === userId) {
      this.expandedUserId.set(null);
      this.abort('sandboxes');
      return;
    }
    this.expandedUserId.set(userId);
    void this.loadUserSandboxes(userId);
  }

  retry(section: Section): void {
    if (section === 'summary') void this.loadSummary();
    else if (section === 'trends') void this.loadTrends();
    else if (section === 'users') void this.loadUsers();
    else if (this.expandedUserId())
      void this.loadUserSandboxes(this.expandedUserId() as string);
  }

  /** Re-reads every period-scoped section with a fresh window ending now. */
  private reloadAll(): void {
    this.window = buildIstWindow(this.periodDays());
    this.page.set(1);
    this.expandedUserId.set(null);
    this.abort('sandboxes');
    void this.loadSummary();
    void this.loadTrends();
    void this.loadUsers();
  }

  private windowQuery() {
    return {
      resourceType: this.resourceType(),
      from: this.window.from,
      to: this.window.to,
    };
  }

  private async loadSummary(): Promise<void> {
    await this.request(
      'summary',
      'Could not load the sandbox overview.',
      (signal) =>
        this.api
          .getPlatformDashboardSummary(this.windowQuery(), signal)
          .then((res) => this.summary.set(res)),
    );
  }

  private async loadTrends(): Promise<void> {
    await this.request(
      'trends',
      'Could not load daily session hours.',
      (signal) =>
        this.api
          .getPlatformDashboardTrends(this.windowQuery(), signal)
          .then((res) => this.trends.set(res)),
    );
  }

  private async loadUsers(): Promise<void> {
    this.expandedUserId.set(null);
    this.abort('sandboxes');
    await this.request('users', 'Could not load usage by user.', (signal) =>
      this.api
        .getPlatformDashboardUsers(
          {
            ...this.windowQuery(),
            q: this.searchQuery().trim().slice(0, 100) || undefined,
            sort: this.sort(),
            page: this.page(),
            size: this.pageSize(),
          },
          signal,
        )
        .then((res) => this.users.set(res)),
    );
  }

  private async loadUserSandboxes(userId: string): Promise<void> {
    this.userSandboxes.set([]);
    await this.request(
      'sandboxes',
      "Could not load this user's sandboxes.",
      (signal) =>
        this.api
          .getPlatformDashboardSandboxes(
            { userId, resourceType: this.resourceType(), size: 100 },
            signal,
          )
          .then((res) => {
            if (this.expandedUserId() !== userId) return;
            this.userSandboxes.set(
              (res?.result ?? []).map((s) => this.toSandboxRow(s)),
            );
          }),
    );
  }

  private toSandboxRow(s: SandboxDashboardSandbox): SandboxRowVm {
    const state = String(s.state || 'unknown');
    return {
      id: s.id,
      name: s.name || s.id,
      resourceType: (s.resourceType || '').toUpperCase() || '—',
      state,
      stateLabel: sandboxStateLabel(state),
      stateTone: sandboxStateTone(state),
      cpu: formatSandboxCores(s.resources?.cpuRequestCores),
      memory: formatSandboxBytes(s.resources?.memoryRequestBytes),
      gpu: formatSandboxGpuUnits(s.resources?.gpuResources),
    };
  }

  /** Runs one section request, cancelling any in-flight request for the same section. */
  private async request(
    section: Section,
    fallbackError: string,
    run: (signal: AbortSignal) => Promise<void>,
  ): Promise<void> {
    this.abort(section);
    const controller = new AbortController();
    this.controllers.set(section, controller);
    this.setLoading(section, true);
    this.setError(section, null);
    try {
      await run(controller.signal);
    } catch (error) {
      if (isAbortError(error)) return;
      this.setError(
        section,
        sandboxDashboardErrorMessage(error, fallbackError),
      );
    } finally {
      if (this.controllers.get(section) === controller) {
        this.controllers.delete(section);
        this.setLoading(section, false);
      }
    }
  }

  private abort(section: Section): void {
    this.controllers.get(section)?.abort();
    this.controllers.delete(section);
    this.setLoading(section, false);
  }

  private setLoading(section: Section, value: boolean): void {
    this.loading.update((state) => ({ ...state, [section]: value }));
  }

  private setError(section: Section, value: string | null): void {
    this.errors.update((state) => ({ ...state, [section]: value }));
  }
}
